import React, { useContext, useState, useRef, useMemo } from 'react'
import { TextField, Autocomplete, IconButton, Tooltip } from "@mui/material"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import { LoadingContext } from '../M_Utils'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import PasswordChecklist from "react-password-checklist"
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

/**
 * Renders a reusable text input component with support for:
 * - standard text input rendering;
 * - autocomplete rendering when options are provided;
 * - optional password visibility toggle;
 * - optional password strength and match validation checklist;
 * - optional label tooltip;
 * - custom visual styling controlled by props and theme context.
 *
 * This component acts as a generic input abstraction used across forms,
 * keeping visual behavior and interaction rules consistent in the application.
 *
 * @param {object} props Component props.
 * @param {string|React.ReactNode} props.label Field label displayed by Material UI.
 * @param {string} props.type Input type, such as text, number, password, or date.
 * @param {string|object|number} props.value Current input value.
 * @param {Function} props.onChange Callback fired when the value changes.
 * @param {boolean} props.disabled Indicates whether the field is disabled.
 * @param {number} props.maxNumbers Maximum character length applied to the html input.
 * @param {boolean} props.required Indicates whether the field is required.
 * @param {string|boolean} props.color Custom primary border/accent color.
 * @param {string|boolean} props.secondColor Custom secondary text color.
 * @param {number|string} props.size Custom field width.
 * @param {string} props.placeholder Placeholder text.
 * @param {string} props.placeholderColor Placeholder text color.
 * @param {object} props.InputProps Additional Material UI input props.
 * @param {Function} props.onKeyDown Callback fired on key down.
 * @param {number|string} props.height Custom field height.
 * @param {string} props.padding Custom input padding.
 * @param {string} props.margin Custom external margin.
 * @param {string} props.backgroundColor Custom input background color.
 * @param {Array} props.options Enables autocomplete mode when provided.
 * @param {Array} props.labelFields Fields used to build the autocomplete option label.
 * @param {Function} props.onChangeValue Callback fired when an autocomplete option is selected.
 * @param {React.ReactElement} props.iconStart Custom start adornment.
 * @param {React.ReactElement} props.iconEnd Custom end adornment.
 * @param {boolean} props.autoFocus Indicates whether the input should autofocus.
 * @param {Function} props.onEnter Callback fired when Enter is pressed.
 * @param {string} props.helperTextMessage Helper text displayed below the field.
 * @param {boolean} props.errorMessage Indicates whether the field is in error state.
 * @param {boolean} props.passVerification Enables password checklist rendering.
 * @param {number} props.minLengthverification Minimum password length for checklist validation.
 * @param {string} props.valueAgain Confirmation password value for match validation.
 * @param {boolean} props.noAutoShrink Controls Material UI label shrink behavior.
 * @param {boolean} props.disabledCheckPasswords Disables password match validation.
 * @param {number|string} props.inputHeight Custom inner input height.
 * @param {string} props.disabledBorderColor Border and text color applied in disabled mode.
 * @param {string} props.minDate Minimum date for date inputs.
 * @param {string} props.maxDate Maximum date for date inputs.
 * @param {Function} props.onBlur Callback fired on blur.
 * @param {string} props.cy Cypress selector attribute.
 * @param {string} props.tooltipMessage Optional tooltip text rendered near the label.
 * @returns {JSX.Element} Input component rendered as a TextField or Autocomplete.
 */
export function MInputTextField({
    label,
    type,
    value,
    onChange,
    disabled,
    maxNumbers,
    required,
    color,
    secondColor,
    size,
    placeholder,
    placeholderColor,
    InputProps,
    onKeyDown,
    height,
    padding,
    margin,
    backgroundColor,
    options,
    labelFields,
    onChangeValue,
    iconStart,
    iconEnd,
    autoFocus,
    onEnter,
    helperTextMessage,
    errorMessage,
    passVerification,
    minLengthverification,
    valueAgain,
    noAutoShrink,
    disabledCheckPasswords,
    inputHeight,
    disabledBorderColor,
    minDate,
    maxDate,
    onBlur,
    cy,
    tooltipMessage
}) {
    const isRequired = required || false
    const ctx = useContext(LoadingContext)
    const [showPassword, setShowPassword] = useState(false)
    const { t } = useTranslation()
    const element = useRef(null)

    /**
     * Resolves the main accent color used by the component.
     *
     * The explicit prop value has priority.
     * When it is not provided, the value falls back to the theme context.
     * A final fallback is used to guarantee a valid color.
     *
     * @returns {string} Resolved primary color.
     */
    function passColor() {
        return color || ctx.primaryColor || "var(--color-black)"
    }
    color = passColor()

    /**
     * Resolves the secondary color used for text and disabled states.
     *
     * The explicit prop value has priority.
     * When it is not provided, the value falls back to the theme context.
     * A final fallback is used to guarantee a valid color.
     *
     * @returns {string} Resolved secondary color.
     */
    function passSecondColor() {
        return secondColor || ctx.secondaryColor || "var(--color-black)"
    }
    secondColor = passSecondColor()

    /**
     * Resolves the background color used inside the input.
     *
     * @returns {string} Resolved background color.
     */
    function passBackgroundColor() {
        return backgroundColor || 'var(--color-white)'
    }
    backgroundColor = passBackgroundColor()

    /**
     * Restricts keyboard input when the field behaves as a numeric input.
     *
     * The function allows:
     * - numeric keys from the top keyboard row;
     * - numpad numeric keys;
     * - common editing keys such as backspace and delete;
     * - decimal and sign-related keys;
     * - navigation keys used while editing;
     * - clipboard shortcuts for copy, paste, and cut.
     *
     * All remaining keys are prevented to keep the input constrained.
     *
     * @param {KeyboardEvent} e Keyboard event raised by the input.
     * @returns {boolean|undefined} Returns true for allowed keys; otherwise prevents the event.
     */
    function isNumber(e) {
        // Allow numeric keys, editing keys, decimal separators, sign keys, tab, and enter.
        if (
            (e.keyCode >= 48 && e.keyCode <= 57) ||
            (e.keyCode >= 96 && e.keyCode <= 105) ||
            e.keyCode === 8 ||
            e.keyCode === 188 ||
            e.keyCode === 9 ||
            e.keyCode === 13 ||
            e.keyCode === 173 ||
            e.keyCode === 189 ||
            e.keyCode === 46 ||
            e.keyCode === 190 ||
            (e.keyCode >= 107 && e.keyCode <= 110) ||
            e.keyCode === 171
        ) {
            return true
        }

        // Allow clipboard shortcuts and horizontal navigation keys.
        if (
            (e.keyCode === 67 && e.ctrlKey) ||
            (e.keyCode === 86 && e.ctrlKey) ||
            (e.keyCode === 88 && e.ctrlKey) ||
            e.keyCode === 37 ||
            e.keyCode === 39
        ) {
            return true
        }

        e.preventDefault()
    }

    /**
     * Builds the label node used by Material UI.
     *
     * When no tooltip message is provided, the original label is returned unchanged.
     * When a tooltip message exists, the label is wrapped with a small information icon
     * that exposes the help text on hover.
     *
     * useMemo is used to avoid rebuilding the label node when the label and tooltip
     * content remain the same across renders.
     */
    const resolvedLabel = useMemo(() => {
        if (!tooltipMessage) return label

        return (
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                }}
            >
                <span>{label}</span>
                <Tooltip
                    title={tooltipMessage}
                    arrow
                    placement="top"
                >
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            cursor: "help",
                            lineHeight: 0,
                        }}
                    >
                        <InfoOutlinedIcon
                            fontSize="inherit"
                            style={{
                                fontSize: 16,
                                color: "var(--color-grey)",
                            }}
                        />
                    </span>
                </Tooltip>
            </span>
        )
    }, [label, tooltipMessage])

    /**
     * Centralizes all Material UI styling rules used by the component.
     *
     * The style object adapts:
     * - label color and emphasis;
     * - disabled border and text color;
     * - placeholder appearance;
     * - focused border and shadow;
     * - input dimensions and spacing.
     *
     * This keeps all visual customization in a single place and avoids style drift
     * between the autocomplete and standard text field branches.
     */
    const styles = {
        margin: margin,
        width: size || null,
        '& label': {
            color: 'var(--color-black)',
            fontWeight: () => { return disabled && disabledBorderColor ? 'bold' : 'auto' },
            wordBreak: 'break-all',
            fontWeight: 'bold'
        },
        '& label.Mui-focused': {
            color: 'var(--color-black)',
            fontWeight: () => { return disabled && disabledBorderColor ? 'bold' : 'auto' }
        },
        '& .MuiInput-underline:after': {
            borderBottomColor: () => { return disabled ? disabledBorderColor : color }
        },
        "& .MuiInputBase-root.Mui-disabled": {
            "& > fieldset": {
                borderColor: () => { return disabled ? disabledBorderColor : 'rgba(0, 0, 0, 0.38) !important' }
            },
            "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: () => { return disabled ? disabledBorderColor : secondColor },
                fontWeight: () => { return disabled && disabledBorderColor ? 'bold' : 'auto' }
            },
        },
        '& .MuiOutlinedInput-input': {
            backgroundColor: backgroundColor,
            padding: padding,
            height: inputHeight || 'auto',
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: () => { return disabled ? disabledBorderColor : color },
                width: size || 'auto',
                height: height,
            },
            '&:hover fieldset': {
                borderColor: () => { return disabled ? disabledBorderColor : color },
            },
            '&.Mui-focused fieldset': {
                borderColor: () => { return disabled ? disabledBorderColor : color },
                boxShadow: `2px 2px 2px ${color}`
            },
            '& input::placeholder': {
                color: placeholderColor || 'rgba(0,0,0,0.4)',
                opacity: 1,
                fontWeight: 500,
            },
            "& legend": {
                // Intentionally left available for future fine-grained legend adjustments.
            }
        },
    }

    /**
     * Builds the input adornments used by the standard TextField branch.
     *
     * The start adornment uses the provided custom icon when the field is enabled.
     * The end adornment uses:
     * - the provided custom icon when available; or
     * - the password visibility toggle when the input type is password.
     *
     * The password visibility toggle is not rendered when the field is disabled.
     *
     * @returns {object} Material UI adornment configuration.
     */
    function adornments() {
        return {
            startAdornment: (!disabled && iconStart || ''),
            endAdornment: (!disabled && iconEnd || type === 'password' ?
                <IconButton
                    disableRipple
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((prev => !prev))}
                    edge="end"
                    sx={{
                        "&.Mui-focusVisible": {
                            color: "var(--color-black)",
                            backgroundColor: "var(--color-grey-light)"
                        },
                    }}
                >
                    {showPassword
                        ? <VisibilityOff style={{ color: 'var(--color-grey)' }} />
                        : <Visibility style={{ color: 'var(--color-grey)' }} />}
                </IconButton>
                : '')
        }
    }

    /**
     * Resolves the effective html input type used by the standard TextField branch.
     *
     * Number fields use "string" to preserve the component's custom keyboard filtering behavior.
     * Password fields toggle between "password" and "text" according to the current visibility state.
     * All remaining types are returned unchanged.
     *
     * @returns {string} Effective input type.
     */
    function inputTypes() {
        if (type === 'number') return 'string'

        if (type === 'password') {
            if (showPassword) {
                return "text"
            }

            return "password"
        }

        return type
    }

    return (
        <>
            {options ?
                <Autocomplete
                    noOptionsText={t("NoOptions")}
                    options={options}
                    sx={styles}
                    getOptionLabel={(e) => {
                        let resultString = ''

                        // Build a comma-separated label using the configured option fields.
                        labelFields.forEach(obj => {
                            if (resultString === '') {
                                resultString = resultString.concat(`${e[obj] || ''}`)
                            } else {
                                resultString = resultString.concat(`, ${e[obj] || ''}`)
                            }
                        })

                        return resultString.trim()
                    }}
                    value={value || null}
                    onChange={onChangeValue}
                    disabled={disabled}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            slotProps={{
                                inputLabel: { shrink: true },
                                input: !disabled ? { ...params.InputProps, ...InputProps } : {}
                            }}
                            data-cy={cy}
                            onChange={onChange}
                            margin="dense"
                            required={isRequired}
                            label={resolvedLabel}
                            variant="outlined"
                            style={{ height: height, textOverflow: 'ellipsis' }}
                            sx={styles}
                            size="small"
                        />
                    )}
                />
                :
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <TextField
                        data-cy={cy}
                        error={errorMessage}
                        helperText={helperTextMessage}
                        sx={styles}
                        autoFocus={autoFocus}
                        placeholder={placeholder}
                        slotProps={{
                            inputLabel: !noAutoShrink
                                ? { autoComplete: "none", shrink: true }
                                : { autoComplete: "none" },
                            htmlInput: {
                                maxLength: maxNumbers,
                                min: minDate,
                                max: maxDate
                            },
                            input: adornments(),
                        }}
                        ref={element}
                        required={isRequired}
                        margin="dense"
                        label={resolvedLabel}
                        variant="outlined"
                        fullWidth
                        type={inputTypes()}
                        disabled={disabled}
                        value={value}
                        onChange={onChange}
                        onKeyDown={(e) => {
                            // Allow explicit enter handling for external consumers.
                            if (onEnter && e.key === 'Enter') {
                                onEnter()
                            }

                            // Blur the input when Escape or Enter is pressed.
                            // This helps close the focused state consistently across custom usages.
                            if (e.key === 'Escape' || e.key === 'Enter') {
                                if (iconStart) {
                                    element.current.childNodes[1].childNodes[1].blur()
                                } else {
                                    element.current.childNodes[1].childNodes[0].blur()
                                }
                                element.current.blur()
                            }

                            // Delegate any extra keyboard behavior to the parent callback.
                            if (onKeyDown) {
                                onKeyDown(e)
                            }

                            // Apply keyboard filtering only when the configured type is numeric.
                            if (type === "number") {
                                isNumber(e)
                            }
                        }}
                        onBlur={onBlur}
                        style={{ height: height }}
                        size="small"
                    />

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        {type === 'password' && passVerification ?
                            <PasswordChecklist
                                rules={disabledCheckPasswords
                                    ? ["minLength", "specialChar", "number", "capital", "lowercase"]
                                    : ["minLength", "specialChar", "number", "capital", "lowercase", "match"]}
                                minLength={minLengthverification}
                                value={value}
                                valueAgain={valueAgain}
                                messages={{
                                    minLength: (
                                        <span>
                                            {t("LabelMaxLength")} {minLengthverification} {t("Labelcaracters")}
                                        </span>
                                    ),
                                    specialChar: (
                                        <span>
                                            {t("LabelSpecialCaracts")}
                                        </span>
                                    ),
                                    number: (
                                        <span>
                                            {t("LabelNeedNumber")}
                                        </span>
                                    ),
                                    capital: (
                                        <span>
                                            {t("LabelUpCarac")}
                                        </span>
                                    ),
                                    lowercase: (
                                        <span>
                                            {t("LabelLowerCase")}
                                        </span>
                                    ),
                                    ...(disabledCheckPasswords
                                        ? {}
                                        : {
                                            match: (
                                                <span>
                                                    {t("LabelPassMatch")}
                                                </span>
                                            )
                                        })
                                }}
                            />
                            : <></>
                        }
                    </div>
                </div>
            }
        </>
    )
}

MInputTextField.propTypes = {
    label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node,
    ]),
    type: PropTypes.string,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.number,
    ]),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    maxNumbers: PropTypes.number,
    minDate: PropTypes.string,
    maxDate: PropTypes.string,
    required: PropTypes.bool,
    color: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
    ]),
    secondColor: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool
    ]),
    size: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    placeholder: PropTypes.string,
    placeholderColor: PropTypes.string,
    InputProps: PropTypes.object,
    onKeyDown: PropTypes.func,
    height: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    padding: PropTypes.string,
    margin: PropTypes.string,
    backgroundColor: PropTypes.string,
    options: PropTypes.array,
    labelFields: PropTypes.array,
    onChangeValue: PropTypes.func,
    iconStart: PropTypes.element,
    iconEnd: PropTypes.element,
    autoFocus: PropTypes.bool,
    onBlur: PropTypes.func,
    onEnter: PropTypes.func,
    helperTextMessage: PropTypes.string,
    errorMessage: PropTypes.bool,
    passVerification: PropTypes.bool,
    minLengthverification: PropTypes.number,
    valueAgain: PropTypes.string,
    noAutoShrink: PropTypes.bool,
    disabledCheckPasswords: PropTypes.bool,
    inputHeight: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    disabledBorderColor: PropTypes.string,
    cy: PropTypes.string,
    tooltipMessage: PropTypes.string,
}